<template>
  <tm-auto-size
    ref="autoSize"
    class="tm-auto-size-panel"
    :class="{ 'tm-auto-size-panel--multiline': isMultiline }"
    :set-max-needed-width="setMaxNeededWidth"
    :justify="justify"
    :is-freeze="isFreeze"
    :min-visible-items="minVisibleItems"
    :max-visible-items="maxVisibleItems"
    :min-hidden-items="minHiddenItems"
    :consider-more-as-visible-item="considerMoreAsVisibleItem"
    :max-visible-lines="maxVisibleLines"
  >
    <slot />

    <template v-slot:more="{ moreHiddenIndexes }">
      <tm-auto-size-panel-item>
        <tm-dropdown
          :menu-test-id="moreMenuTestId"
          :model-value="dropdownModelValue"
          :position="dropdownPosition"
          :close-on-content-click="dropdownCloseOnClick"
          :close-on-outside-click="dropdownCloseOnOutsideClick"
          @update:model-value="$emit('update:dropdown-model-value', $event)"
        >
          <slot
            name="dropdown-activator"
            :visible-items-count="moreHiddenIndexes.length + 1"
          />

          <template v-slot:menu>
            <tm-auto-size-children-viewer :hidden-children-indexes="moreHiddenIndexes">
              <slot />
            </tm-auto-size-children-viewer>
          </template>
        </tm-dropdown>
      </tm-auto-size-panel-item>
    </template>
  </tm-auto-size>
</template>

<script lang="ts" setup>
/**
 * The component hides elements that do not fit in the container and move them to the more dropdown.
 *
 * Example:
 * <tm-auto-size-panel>
 *   <tm-auto-size-panel-item>
 *     <tm-button>Button 1</tm-button>
 *   </tm-auto-size-panel-item>
 *   <tm-auto-size-panel-item hiding-priority="alwaysVisible">
 *     <tm-button>Button 2</tm-button>
 *   </tm-auto-size-panel-item>
 * </tm-auto-size-panel>
 */

import { computed, useTemplateRef } from '@/composition/vue/compositionApi'
import TmAutoSize from '@/components/shared/autoSize/TmAutoSize.vue'
import TmAutoSizeChildrenViewer from '@/components/shared/autoSize/TmAutoSizeChildrenViewer.vue'
import TmAutoSizePanelItem from '@/components/shared/autoSize/panel/TmAutoSizePanelItem.vue'
import TmDropdown from '@/components/shared/TmDropdown.vue'
import type { ControlPosition } from '@/composition/tooltip/renderTooltip'

const {
  dropdownCloseOnClick = true,
  dropdownCloseOnOutsideClick = true,
  moreMenuTestId = 'tm-auto-size-panel-more-dropdown',
  maxVisibleLines = 1,
} = defineProps<{
  dropdownModelValue?: boolean
  dropdownPosition?: ControlPosition
  dropdownCloseOnClick?: boolean
  dropdownCloseOnOutsideClick?: boolean
  setMaxNeededWidth?: boolean
  justify?: 'start' | 'center' | 'end'
  isFreeze?: boolean
  minVisibleItems?: number
  maxVisibleItems?: number
  minHiddenItems?: number
  considerMoreAsVisibleItem?: boolean
  maxVisibleLines?: number
  moreMenuTestId?: string
}>()

defineEmits<{
  'update:dropdown-model-value': [boolean]
}>()

const autoSizeRef = useTemplateRef<InstanceType<typeof TmAutoSize>>('autoSize')

const isMultiline = computed(() => maxVisibleLines > 1)

defineExpose({
  listRef: computed(() => autoSizeRef.value?.containerRef),
})
</script>

<style lang="scss" scoped>
.tm-auto-size-panel {
  --tm-auto-size-panel-gap: 8px;
  margin-left: calc(var(--tm-auto-size-panel-gap) / 2 * -1);
  margin-right: calc(var(--tm-auto-size-panel-gap) / 2 * -1);

  &--multiline {
    row-gap: var(--tm-auto-size-panel-gap);
  }
}
</style>
